import { getExtension } from '../../utils/audioFile';
import { AudioInputError } from './AudioInput.types';

export interface ValidateFileOptions {
    maxSizeBytes?: number;
    allowedExtensions?: string[];
    allowedMimePrefixes?: string[];
}

const DEFAULT_MAX_SIZE_BYTES = 50 * 1024 * 1024;
const DEFAULT_MIME_PREFIXES = ['audio/'];

export function validateAudioFile(file: File, opts: ValidateFileOptions = {}): AudioInputError | null {
    const {
        maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
        allowedExtensions,
        allowedMimePrefixes = DEFAULT_MIME_PREFIXES,
    } = opts;

    const mimeOk = !!file.type && allowedMimePrefixes.some(p => file.type.startsWith(p));
    const extOk = !allowedExtensions || allowedExtensions.includes(getExtension(file.name));
    if (!mimeOk && !extOk) {
        return { code: 'invalid-type', message: 'Only audio files are allowed.', file };
    }

    if (file.size > maxSizeBytes) {
        return {
            code: 'file-too-large',
            message: `File is too large. Max size is ${(maxSizeBytes / (1024 * 1024)).toFixed(0)}MB.`,
            file,
        };
    }

    return null;
}

export interface ValidateDurationOptions {
    minDurationSec?: number;
    maxDurationSec?: number;
}

function formatDurationLimit(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs === 0 ? `${mins} min` : `${mins} min ${secs}s`;
}

export function validateAudioDuration(durationSec: number, opts: ValidateDurationOptions = {}): AudioInputError | null {
    if (opts.minDurationSec != null && durationSec < opts.minDurationSec) {
        return { code: 'too-short', message: `Audio is too short. Minimum ${formatDurationLimit(opts.minDurationSec)} required.` };
    }
    if (opts.maxDurationSec != null && durationSec > opts.maxDurationSec) {
        return { code: 'too-long', message: `Audio is too long. Maximum ${formatDurationLimit(opts.maxDurationSec)} allowed.` };
    }
    return null;
}

export function validateFileCount(currentCount: number, incomingCount: number, maxFiles: number): AudioInputError | null {
    if (currentCount + incomingCount > maxFiles) {
        return { code: 'max-files-exceeded', message: `You can only add up to ${maxFiles} file(s).` };
    }
    return null;
}
